import { BLEND_MODES, Container, Graphics } from "pixi.js"
import { BasicSprite } from "../sprites/basic_sprite"

// The player's health, as a row of hearts docked to the readied-item tray (gui_manager's
// positionVitals).
//
// What it replaced, and why: the old meter rebuilt all five sprites from scratch on every change,
// drew each heart as one of three frames, and said "you are nearly dead" by running a Throbber over
// the WHOLE row -- a 25ms setInterval strobing `alpha` between 0.4 and 1.5. That is a bad bargain
// twice over. A strobe makes the thing you most need to read disappear half the time, and a
// wall-clock interval keeps firing in a backgrounded tab, where no frame will ever draw it.
//
// Three ideas replace it:
//
//  - **The art is the game's, the LIGHT is drawn.** The heart stays the 15x13 pixel sprite, because
//    a vector heart is a different medium sitting on a pixel world. Everything that glows -- the
//    beat halo, the hit bloom, the burst -- is Graphics and additive blending, which read as light
//    rather than as art from somewhere else. No filters: this game's hardware target cannot spare a
//    filter pass, so a soft edge is a fatter, fainter copy of the same shape.
//
//  - **Hearts DRAIN rather than snap.** A heart is the `empty` frame (the vessel) with the `full`
//    frame masked over it, revealed from the bottom. The fill is a continuous 0..1 that eases to
//    its new value, so taking half a heart is something you watch happen. The mask is dropped
//    whenever a heart is wholly full or wholly empty, so in practice at most ONE is ever stencilled.
//
//  - **Low health BEATS, it does not blink.** The remaining hearts pulse on a lub-dub rhythm that
//    quickens as health falls, over a red halo. The row never drops below full opacity, so the
//    count stays readable at the exact moment it matters most.
//
// Nothing here owns a timer: `animate` is handed the frame delta, and does no work at all on a
// resting meter.

// Heart art is 15x13 (humanoid.json). The pitch gives FOUR pixels between hearts, which is the same
// as the plate's padding -- at a one-pixel gap the row read as cramped in the middle and loose at
// the ends, which is what an uneven rhythm looks like even when everything is perfectly centred.
const HEART_W = 15
const HEART_H = 13
const PITCH = HEART_W + 4
// Padding around the row's ART (not around the slot grid, which is wider than the art it holds --
// measuring from the grid is what made the end margins disagree with the gaps). It is also what
// keeps getLocalBounds CONSTANT: the plate is static and must stay the widest thing here, or a
// beating heart would resize the meter and positionVitals would shuffle the meters beside it.
const PLATE_PAD = 4
const PLATE_RADIUS = 5
// The plate's dropped silhouette. One pixel: at more than that the shadow reads as PLATE, the
// bottom edge looks thicker than the top, and the hearts look like they are sitting high.
const PLATE_SHADOW_DROP = 1

// OPTICAL centring, which is not the same as geometric centring. A heart is TOP-HEAVY: wide lobes
// above, tapering to a single pixel below -- 123 opaque pixels in the 15x13 frame with their
// centroid on row 5.3, so the ink's mass sits ~1.2px above the centre of its bounding box. Centre
// the box exactly, as this did at first, and the row reads as riding high with too much air under
// it. The geometry was perfect and it still looked wrong.
//
// Half the centroid offset, not all of it: at the full 1.2 the hearts read LOW, because that
// tapering tip still counts as the bottom edge to the eye even though it weighs almost nothing.
//
// And it is the PLATE that moves, not the art. Nudging a 15x13 pixel sprite by a fraction of a
// pixel is how pixel art goes soft; a vector rounded-rect can sit anywhere it likes.
const PLATE_OPTICAL_LIFT = 0.6

// The readied-item tray's own palette (gui/equipped.ts): the hearts sit directly on top of it and
// the two have to read as one cluster rather than two overlays.
const PLATE_FILL = 0x1b1512
const PLATE_ALPHA = 0.55
const PLATE_RIM = 0x6b4f38

const DANGER_COLOR = 0xff3b30
const HEAL_COLOR = 0xffd166

// How long a whole heart takes to drain. Long enough to see, short enough that a burst of damage
// does not leave the meter lying about how much health is left.
const DRAIN_MS = 260
// The flash thrown off a heart that just changed, and the whole row's recoil.
const BURST_MS = 320
const HIT_MS = 300
const HIT_SHAKE = 2.5

// At or below this many hearts the meter beats.
export const DANGER_HEARTS = 2
const BEAT_SLOW_MS = 880
const BEAT_FAST_MS = 440
const BEAT_SCALE = 0.16

// Non-finite in, zero out. A NaN here does not throw -- it rides all the way into a mask rectangle
// with NaN coordinates, which pixi draws as NO geometry, and an empty stencil clips NOTHING: the
// hearts come out FULL. A meter that reads full because its input was broken is the worst possible
// failure for this particular widget.
const clamp01 = (value: number): number => (Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0)

/** How full slot `index` is, 0..1, given the health in HEARTS (not hit points). */
export const slotFill = (hearts: number, index: number): number => clamp01(hearts - index)

/**
 * How fast the heart beats with `hearts` left, in ms per beat. Quickens all the way down, so the
 * last half-heart is audibly (visibly) frantic rather than merely "in the danger band".
 */
export const beatPeriodMs = (hearts: number): number => {
    const danger = clamp01(hearts / DANGER_HEARTS)
    return BEAT_FAST_MS + (BEAT_SLOW_MS - BEAT_FAST_MS) * danger
}

/**
 * The beat itself, 0..1, over one period. Two thumps and a rest -- a heartbeat, not a sine wave:
 * an even pulse reads as a breathing UI element, and this has to read as a body in trouble.
 */
export const heartbeatPulse = (phase: number): number => {
    const at = phase - Math.floor(phase)
    const thump = (centre: number, width: number): number => {
        const distance = Math.abs(at - centre)
        if (distance >= width) {
            return 0
        }
        const t = 1 - distance / width
        return t * t
    }
    return Math.max(thump(0.06, 0.08), thump(0.24, 0.07) * 0.6)
}

interface Slot {
    // Origin is the heart's CENTRE, so a beat scales about the middle of the art whatever pivot the
    // atlas gave the frame.
    root: Container
    vessel: BasicSprite
    fill: BasicSprite
    fillMask: Graphics
    burst: BasicSprite
    // Art size, measured once from the loaded texture.
    width: number
    height: number
    // Where the fill is, and where it is heading. Eased apart is the whole drain.
    shown: number
    target: number
    burstMs: number
    burstColor: number
}

export class IconMeter extends Graphics {
    max: number
    current: number
    private iconBase: string
    private slots: Slot[] = []
    private hearts = 0
    private beatMs = 0
    private hitMs = 0
    // How wide the plate is told to be. Undefined means hug the hearts; the vitals row sets it so
    // ONE plate runs under the hearts AND the bars beside them, instead of the hearts sitting on a
    // panel while the bars float on the grass next to it.
    private plateSpan?: number
    private needsDraw = true

    constructor(iconBase: string, max: number) {
        super()
        this.iconBase = iconBase
        this.max = max
    }

    /**
     * New health, on the caller's 0..`max` scale (two per heart).
     *
     * Only records what changed and what should flash for it; nothing moves until `animate` runs,
     * so a value arriving twice in a frame cannot double up an effect.
     */
    update = (amt: number) => {
        if (this.max < 1) {
            return
        }
        if (amt === this.current) {
            return
        }
        this.build()
        const before = this.hearts
        const after = Number.isFinite(amt) ? Math.max(0, amt / 2) : 0
        this.current = amt
        this.hearts = after

        if (after < before) {
            this.hitMs = HIT_MS
        }
        this.slots.forEach((slot, index) => {
            const was = slotFill(before, index)
            const now = slotFill(after, index)
            slot.target = now
            if (now === was) {
                return
            }
            // A heart that changed throws a flash of its own colour: white-hot for damage, gold for
            // a heal. Damage also snaps the fill AHEAD of the drain by nothing -- the drain is what
            // sells it -- so the burst is the only instant part.
            slot.burstMs = BURST_MS
            slot.burstColor = now < was ? 0xffffff : HEAL_COLOR
        })
        this.needsDraw = true
    }

    /**
     * Advance one frame. `deltaMs` is the render loop's own delta.
     *
     * Returns without touching the display list when nothing is moving, which is the common case:
     * a healthy player's meter costs one comparison a frame.
     */
    animate = (deltaMs: number) => {
        if (!this.slots.length) {
            return
        }
        let moving = false

        const step = deltaMs / DRAIN_MS
        for (const slot of this.slots) {
            if (slot.shown !== slot.target) {
                // Linear and clamped, never an exponential approach: a filter that only ever halves
                // the remainder never actually lands, and a heart that is forever 2% full is a lie
                // about how much health is left.
                slot.shown = slot.shown > slot.target
                    ? Math.max(slot.target, slot.shown - step)
                    : Math.min(slot.target, slot.shown + step)
                moving = true
            }
            if (slot.burstMs > 0) {
                slot.burstMs = Math.max(0, slot.burstMs - deltaMs)
                moving = true
            }
        }

        if (this.hitMs > 0) {
            this.hitMs = Math.max(0, this.hitMs - deltaMs)
            moving = true
        }

        const beating = this.hearts > 0 && this.hearts <= DANGER_HEARTS
        if (beating) {
            this.beatMs += deltaMs
            moving = true
        } else if (this.beatMs !== 0) {
            this.beatMs = 0
            moving = true
        }

        if (!moving && !this.needsDraw) {
            return
        }
        this.needsDraw = false
        this.redraw(beating)
    }

    /**
     * Widen the plate to cover the whole vitals row, or pass undefined to go back to hugging the
     * hearts. Only the WIDTH moves, never the plate's own left or top, so a caller can place this
     * widget by `plateRect()` first and size the plate afterwards without the two chasing each other.
     */
    public setPlateWidth = (width?: number) => {
        if (this.plateSpan === width) {
            return
        }
        this.plateSpan = width
        this.needsDraw = true
        this.redraw(false)
    }

    /** The plate's rectangle in this widget's own space — what a caller lines up against. */
    public plateRect = () => {
        const art = this.artExtent()
        return {
            x: art.left - PLATE_PAD,
            y: -(art.height / 2 + PLATE_PAD) - PLATE_OPTICAL_LIFT,
            width: this.plateSpan ?? art.right - art.left + PLATE_PAD * 2,
            height: art.height + PLATE_PAD * 2,
        }
    }

    /**
     * The hearts' own resting rectangle, art only. Deliberately NOT getLocalBounds: a beating or
     * bursting heart is scaled up to 1.9, so anything laying a row out against measured bounds would
     * be shoved sideways by a hit.
     */
    public artRect = () => {
        const art = this.artExtent()
        return { x: art.left, y: -art.height / 2, width: art.right - art.left, height: art.height }
    }

    /** Test seam: the fill each slot is currently SHOWING, which lags the value while it drains. */
    public shownFill = (): number[] => this.slots.map(slot => slot.shown)

    // ---------------------------------------------------------------- building

    // Built once, when the first real `max` arrives. The old meter made five fresh BasicSprites on
    // every hit -- each of which carries a click area and a Text -- and threw the previous five away.
    private build = () => {
        const wanted = Math.floor(this.max / 2)
        if (this.slots.length === wanted) {
            return
        }
        this.slots.forEach(slot => this.removeChild(slot.root))
        this.slots = []

        for (let index = 0; index < wanted; index++) {
            const root = new Container()
            const vessel = new BasicSprite({ name: `${this.iconBase}-empty.png`, scale: 1.0 })
            const fill = new BasicSprite({ name: `${this.iconBase}-full.png`, scale: 1.0 })
            const burst = new BasicSprite({ name: `${this.iconBase}-full.png`, scale: 1.0 })

            // Centre each piece of art on the slot's origin, measured rather than assumed: the atlas
            // frames carry a TexturePacker pivot and the three need not share it. Written out
            // rather than with `||` defaults -- an already-centred sprite measures -0, which is
            // FALSY, so `a || b` silently takes the other branch on exactly the common case.
            const bounds = vessel.getLocalBounds()
            const width = bounds.width > 0 ? bounds.width : HEART_W
            const height = bounds.height > 0 ? bounds.height : HEART_H
            for (const art of [vessel, fill, burst]) {
                const own = art.getLocalBounds()
                if (own.width > 0) {
                    art.x = -(own.x + own.width / 2)
                    art.y = -(own.y + own.height / 2)
                }
            }
            if (burst.sprite) {
                // Additive, so the flash reads as the heart lighting UP. A tint can only ever
                // darken, so there is no way to flash a sprite white by tinting it.
                burst.sprite.blendMode = BLEND_MODES.ADD
            }
            burst.visible = false

            const fillMask = new Graphics()
            root.addChild(vessel, fill, burst, fillMask)

            root.x = index * PITCH + PITCH / 2
            this.addChild(root)
            this.slots.push({
                root, vessel, fill, fillMask, burst,
                width, height,
                shown: slotFill(this.hearts, index),
                target: slotFill(this.hearts, index),
                burstMs: 0,
                burstColor: 0xffffff,
            })
        }
    }

    // ---------------------------------------------------------------- drawing

    // The rectangle the ART actually occupies, which is what the plate is drawn around. The slot
    // grid is wider than its art (a slot is PITCH, the heart is HEART_W), so padding the grid puts
    // half a gap of extra air at each end and the row stops looking evenly spaced.
    private artExtent = () => {
        const width = this.slots[0]?.width ?? HEART_W
        const height = this.slots[0]?.height ?? HEART_H
        return {
            left: PITCH / 2 - width / 2,
            right: (this.slots.length - 1) * PITCH + PITCH / 2 + width / 2,
            height,
        }
    }

    private redraw = (beating: boolean) => {
        const pulse = beating ? heartbeatPulse(this.beatMs / beatPeriodMs(this.hearts)) : 0
        const hit = this.hitMs / HIT_MS

        // The recoil rides on PIVOT, not position: pivot is part of this object's own transform, so
        // getLocalBounds -- and therefore where positionVitals puts the meters beside this one --
        // never sees the shake.
        this.pivot.x = hit > 0 ? Math.sin((1 - hit) * Math.PI * 5) * HIT_SHAKE * hit : 0

        this.clear()
        this.drawPlate()
        this.drawBloom(hit)

        this.slots.forEach(slot => {
            const beats = beating && slot.shown > 0
            slot.root.scale.set(1 + (beats ? BEAT_SCALE * pulse : 0))
            if (beats && pulse > 0) {
                this.drawHalo(slot, pulse)
            }
            this.layoutFill(slot)
            this.layoutBurst(slot)
        })
    }

    private drawPlate = () => {
        if (!this.slots.length) {
            return
        }
        const { x, y, width, height } = this.plateRect()
        this.beginFill(0x000000, 0.3)
        this.drawRoundedRect(x, y + PLATE_SHADOW_DROP, width, height, PLATE_RADIUS)
        this.endFill()
        this.lineStyle({ width: 1, color: PLATE_RIM, alpha: 0.35 })
        this.beginFill(PLATE_FILL, PLATE_ALPHA)
        this.drawRoundedRect(x, y, width, height, PLATE_RADIUS)
        this.endFill()
        this.lineStyle(0)
    }

    // A red wash over the plate as a hit lands. Kept INSIDE the plate's own rectangle so it cannot
    // widen the meter's bounds.
    private drawBloom = (hit: number) => {
        if (hit <= 0 || !this.slots.length) {
            return
        }
        // Scoped to the HEARTS' own span, not the whole plate: once the plate runs under the mana
        // and hunger bars too, washing all of it red would say the bars had been hit as well.
        const art = this.artExtent()
        const height = art.height + PLATE_PAD * 2
        this.beginFill(DANGER_COLOR, 0.42 * hit)
        this.drawRoundedRect(art.left - PLATE_PAD, -height / 2 - PLATE_OPTICAL_LIFT,
            art.right - art.left + PLATE_PAD * 2, height, PLATE_RADIUS)
        this.endFill()
    }

    // The glow under a beating heart: three stacked discs, brightest in the middle. The cheap way to
    // a radial falloff when a blur filter is off the table.
    private drawHalo = (slot: Slot, pulse: number) => {
        const radius = Math.max(slot.width, slot.height) * 0.62
        for (let ring = 0; ring < 3; ring++) {
            this.beginFill(DANGER_COLOR, 0.1 * pulse * (1 + ring * 0.5))
            this.drawCircle(slot.root.x, 0, radius * (1 - ring * 0.22))
            this.endFill()
        }
    }

    // Reveal the full-heart art from the bottom up. The mask is dropped at both extremes, so a
    // healthy row carries no stencil work at all and a draining one carries exactly one.
    private layoutFill = (slot: Slot) => {
        if (!(slot.shown > 0)) {
            slot.fill.visible = false
            slot.fill.mask = null
            slot.fillMask.clear()
            return
        }
        slot.fill.visible = true
        if (slot.shown >= 1) {
            slot.fill.mask = null
            slot.fillMask.clear()
            return
        }
        const top = slot.height / 2 - slot.height * slot.shown
        slot.fillMask.clear()
        slot.fillMask.beginFill(0xffffff, 1)
        slot.fillMask.drawRect(-slot.width, top, slot.width * 2, slot.height)
        slot.fillMask.endFill()
        slot.fill.mask = slot.fillMask
    }

    private layoutBurst = (slot: Slot) => {
        if (slot.burstMs <= 0) {
            slot.burst.visible = false
            return
        }
        const age = 1 - slot.burstMs / BURST_MS
        slot.burst.visible = true
        slot.burst.alpha = (1 - age) * 0.9
        slot.burst.scale.set(1 + age * 0.9)
        slot.burst.recolor(slot.burstColor)
    }
}
